import React, {useContext} from 'react';
import {observer} from "mobx-react";
import {StoreContext} from "../../../stores/StoreLoader";
import {ClassroomButton} from "./ClassroomButton";
import {LocationsCard} from "./LocationsCard";
import {TeacherBio} from "./TeacherBio";
import SchoolBlocksLoadingIndicator from "../../utilities/SchoolBlocksLoadingIndicator";
import useSWR from 'swr';
import WatsonApi from "../../../backends/WatsonApi";

export interface IClassroom {
    id: string,
    title: string,
    linkedClassroomType: string,
    isExternalClassroom: boolean,
    classroomUrl: string,
    isLinkedClassroomSuggestion: boolean,
    blockCount: number,
    isLinkedClassroom: boolean,
}

const UserSettings = observer((props: {
    blockObj: IPersonBlock,
    isPublicInfo: boolean
}) => {
    const {i18nStore, userStore, organizationStore} = useContext(StoreContext);
    const {blockObj} = props;

    const canEdit = userStore.admin || userStore.id === blockObj.blockModel.id;
    const canEditContact = userStore.admin

    const cacheKey = `viewPerson-${blockObj.blockModel.id}`;
    const {data, mutate} = useSWR(cacheKey, async () => {
        const client = await WatsonApi();
        const result = await client.apis.organizations.organizations_teachers_read({
            organization_pk: organizationStore.currentOrganization.id,
            id: blockObj.blockModel.id,
            expand: [
                "classrooms",
                "blockCount",
                "staffMemberProfiles.organization",
                "staffMemberProfiles.department"
            ],
        });
        const parsed = JSON.parse(result.data);
        const newModel = parsed.staff;

        props.blockObj.setAttributes({
            blockModel: {
                ...props.blockObj.blockModel,
                staffMemberProfiles: newModel.staffMemberProfiles,
                email: newModel.email,
            },
        });

        return {
            classrooms: parsed.classrooms,
            hasProfileAtThisLocation: !!newModel.staffMemberProfiles.find(s => s.organization.id === organizationStore.organization.id),
        }
    }, {
        revalidateOnFocus: false,
        revalidateIfStale: false,
    });

    async function setClassrooms(fn) {
        if (data) {
            const newClassrooms = fn(data.classrooms);
            await mutate( {...data, classrooms: newClassrooms}, {revalidate: false})
        }
    }

    return <div id={`sb-teacher-modal-${blockObj.blockModel.id}`}
                className={`sb-teacher-modal ${canEdit ? 'sb-edit-mode' : ''}`}>
        <TeacherBio canEdit={canEdit} blockObj={blockObj} canEditContact={canEditContact} isPublicInfo={props.isPublicInfo}/>
        <hr style={{borderTop: "1px solid rgba(0, 0, 0, 0.3)"}}/>
        <div className="row sb-teacher-classes sb-teacher-class-list">
            {data ? data.classrooms/*
                * Display classroom button in following 4 conditions.
                * 1 - Button should appear if it's External Classroom Link.
                * 2 - Button should appear if it's Linked Classroom Link (Google Classroom).
                * 3 - Button should appear if the person is an editor.
                * 4 - Button should appear when there is at least 1 block.
                */
                .filter(classroom => classroom.isExternalClassroom || classroom.isLinkedClassroom || canEdit || classroom.blockCount > 0)
                .map(classroom => <ClassroomButton
                    canEdit={canEdit}
                    key={classroom.id}
                    classroom={classroom}
                    type={"edit"}
                    teacherId={blockObj.blockModel.id}
                    setClassrooms={setClassrooms}
                />) : <div style={{textAlign: "center"}}>
                    <SchoolBlocksLoadingIndicator fill={"#E0E0E0"} />
                </div>}
        </div>
        <div className="row sb-teacher-classes">
            {canEdit && data && data.hasProfileAtThisLocation && <>
                <ClassroomButton
                    canEdit={canEdit}
                    type={"new-classblocks"}
                    teacherId={blockObj.blockModel.id}
                    setClassrooms={setClassrooms}
                />
                <ClassroomButton canEdit={canEdit} setClassrooms={setClassrooms} teacherId={blockObj.blockModel.id} type={"new-page"}/>
            </>}
        </div>
        {canEdit && <div className="row modal-card-locations">
            <LocationsCard blockObj={blockObj}/>
        </div>}
    </div>
});

export default UserSettings;
